/**
 * Utility class that reconstructs newline-delimited JSON (`\n\n` by default)
 * from an arbitrary sequence of string chunks. The backend guarantees that
 * every JSON object is followed by the delimiter so we can safely accumulate
 * partial chunks until the delimiter is encountered.
 */

export const DEFAULT_DELIMITER = '\n\n';
export const DEFAULT_MAX_BUFFER_SIZE = 10 * 1024 * 1024; // 10MB default

export class BufferOverflowError extends Error {
  constructor(size: number, maxSize: number) {
    super(`Buffer overflow: ${size} bytes exceeds maximum of ${maxSize} bytes`);
    this.name = 'BufferOverflowError';
  }
}

export class DelimitedJsonParser {
  private buffer = '';
  private readonly delimiter: string;
  private readonly maxBufferSize: number;

  constructor(
    delimiter = DEFAULT_DELIMITER,
    maxBufferSize = DEFAULT_MAX_BUFFER_SIZE
  ) {
    this.delimiter = delimiter;
    this.maxBufferSize = maxBufferSize;
  }

  push(chunk: string): string[] {
    this.buffer += chunk;

    // Check for buffer overflow
    if (this.buffer.length > this.maxBufferSize) {
      throw new BufferOverflowError(this.buffer.length, this.maxBufferSize);
    }

    const messages: string[] = [];
    let index = this.buffer.indexOf(this.delimiter);
    while (index !== -1) {
      const part = this.buffer.slice(0, index);
      if (part.trim()) {
        messages.push(part);
      }
      this.buffer = this.buffer.slice(index + this.delimiter.length);
      index = this.buffer.indexOf(this.delimiter);
    }
    return messages;
  }

  flush(): string[] {
    const final = this.buffer.trim();
    this.buffer = '';
    return final ? [final] : [];
  }
}
